import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LoadingScreen from "components/LoadingScreen";
import AddressAutocomplete, {
  ResolvedAddress,
} from "./AddressAutocomplete";
import ArticlesSection, { SelectedArticle } from "./ArticlesSection";
import PlanningCalendar from "./PlanningCalendar";
import PhoneField from "./PhoneField";
import StepperHeader from "./StepperHeader";
import LegalModal from "./LegalModal";
import CookieBanner from "./CookieBanner";
import {
  PageContainer,
  PageTitle,
  PrimaryButton,
  SecondaryButton,
  SectionCard,
  SectionLead,
  SectionTitle,
  TOKENS,
  TotalsBar,
} from "./styled";
import {
  SERVICE_EN_DIRECT_CARRIER,
  SERVICE_EN_DIRECT_CHECK_ADDRESS,
  SERVICE_EN_DIRECT_PLANNING,
  SERVICE_EN_DIRECT_RESERVE,
  SERVICE_EN_DIRECT_START_CHECKOUT,
  SERVICE_EN_DIRECT_CONFIRM_FREE,
  SERVICE_EN_DIRECT_FINALIZE_PAYMENT,
} from "./graphql";
import type { Carrier, ClientInfo } from "./types";

type SelectedSlot = {
  date: string;
  halfday: string;
  hourFrom: string | null;
  hourTo: string | null;
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    background: TOKENS.GRAY_100,
    transition: "all 0.2s",
    "&:hover": { background: TOKENS.WHITE },
    "&.Mui-focused": {
      background: TOKENS.WHITE,
      "& fieldset": {
        borderColor: TOKENS.ACCENT_BLUE,
        borderWidth: 2,
      },
    },
    "& fieldset": { borderColor: TOKENS.GRAY_300 },
  },
  "& .MuiInputLabel-root": {
    color: TOKENS.GRAY_500,
    "&.Mui-focused": { color: TOKENS.ACCENT_BLUE },
  },
};

const LEGAL_DOCS = [
  { key: "mentions_legales", label: "Mentions légales", title: "Mentions légales" },
  { key: "cgv", label: "CGV", title: "Conditions générales de vente" },
  {
    key: "politique_confidentialite",
    label: "Politique de confidentialité",
    title: "Politique de confidentialité",
  },
] as const;

type LegalKey = (typeof LEGAL_DOCS)[number]["key"];

// A Quill document with only empty markup (e.g. "<p><br></p>") has no real
// text — treat it as absent so we don't show a link to an empty document.
const legalHasContent = (html: string | null | undefined): boolean =>
  !!html && html.replace(/<[^>]*>/g, "").trim().length > 0;

const ServiceEnDirect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const paymentStatus = searchParams.get("payment");
  const returnedReservationId = searchParams.get("rid");

  const [activeStep, setActiveStep] = useState<1 | 2 | 3 | 4>(1);

  const [legalDoc, setLegalDoc] = useState<LegalKey | null>(null);

  // Page-scoped SEO title/description. Mutates the global <title> and meta
  // description only while this route is mounted, then restores the portal
  // defaults on unmount so other portal pages are never affected.
  useEffect(() => {
    const prevTitle = document.title;
    const metaEl = document.querySelector('meta[name="description"]');
    const prevDesc = metaEl?.getAttribute("content") ?? null;

    document.title =
      "Montage & installation de meubles à domicile – Devis et réservation";
    metaEl?.setAttribute(
      "content",
      "Réservez votre prestation de montage de meuble à domicile : vérification de couverture, devis transparent et réservation immédiate en ligne.",
    );

    return () => {
      document.title = prevTitle;
      if (metaEl && prevDesc !== null) metaEl.setAttribute("content", prevDesc);
    };
  }, []);

  const [resolvedAddress, setResolvedAddress] =
    useState<ResolvedAddress | null>(null);
  const [coverage, setCoverage] = useState<any>(null);
  const [coverageChecked, setCoverageChecked] = useState(false);

  const [selectedArticles, setSelectedArticles] = useState<SelectedArticle[]>(
    [],
  );
  const [selectedSlot, setSelectedSlot] = useState<SelectedSlot | null>(null);

  const [client, setClient] = useState<Partial<ClientInfo>>({
    hasElevator: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // selectedContractorId drives which carrier we fetch. null = default
  // (first SED contractor by name, used on page load). After the address
  // coverage check finds the closest depot, this flips to that contractor.
  const [selectedContractorId, setSelectedContractorId] = useState<string | null>(
    null,
  );
  const { data, loading, error } = useQuery(SERVICE_EN_DIRECT_CARRIER, {
    variables: { id: selectedContractorId },
    fetchPolicy: "network-only",
  });

  const [checkAddress, { loading: checkingAddress }] = useLazyQuery(
    SERVICE_EN_DIRECT_CHECK_ADDRESS,
    { fetchPolicy: "network-only" },
  );

  const [loadPlanning, { data: planningData, loading: planningLoading }] =
    useLazyQuery(SERVICE_EN_DIRECT_PLANNING, {
      fetchPolicy: "network-only",
    });

  const [reserve] = useMutation(SERVICE_EN_DIRECT_RESERVE);
  const [startCheckout] = useMutation(SERVICE_EN_DIRECT_START_CHECKOUT);
  const [confirmFree] = useMutation(SERVICE_EN_DIRECT_CONFIRM_FREE);

  const carrier: Carrier | null = data?.serviceEnDirectCarrier ?? null;

  const total = selectedArticles.reduce(
    (sum, s) => sum + s.service.price * s.quantity,
    0,
  );

  const expandedArticleIds = useMemo(
    () =>
      selectedArticles.flatMap(({ service, quantity }) =>
        Array.from({ length: quantity }, () => service.id),
      ),
    [selectedArticles],
  );

  const step1Done = Boolean(coverage?.covered && resolvedAddress);
  const step2Done = selectedArticles.length > 0;
  const step3Done = Boolean(selectedSlot);

  const missingClientFields = useMemo(() => {
    const missing: string[] = [];
    if ((client.firstName ?? "").trim() === "") missing.push("Prénom");
    if ((client.lastName ?? "").trim() === "") missing.push("Nom");
    if (!/^\+\d{1,4}\s\d{6,14}$/.test((client.phone ?? "").trim()))
      missing.push("Téléphone");
    if (!/^\S+@\S+\.\S+$/.test(client.email ?? "")) missing.push("Email");
    if (client.floor === undefined || client.floor === null)
      missing.push("Étage");
    return missing;
  }, [client]);

  const canConfirm = useMemo(() => {
    return (
      step1Done && step2Done && step3Done && missingClientFields.length === 0
    );
  }, [step1Done, step2Done, step3Done, missingClientFields]);

  // Load planning when entering step 3 with a postal code + articles.
  React.useEffect(() => {
    if (
      activeStep === 3 &&
      coverage?.postal_code_id &&
      expandedArticleIds.length > 0
    ) {
      loadPlanning({
        variables: {
          postal_code_id: coverage.postal_code_id,
          article_ids: expandedArticleIds,
          weeks: 4,
          contractor_id: coverage.contractor_id ?? null,
        },
      });
    }
  }, [activeStep, coverage, expandedArticleIds, loadPlanning]);

  const handleCheckAddress = React.useCallback(
    async (addr: ResolvedAddress) => {
      setCoverageChecked(false);
      const res = await checkAddress({
        variables: {
          address: {
            address: addr.address,
            city: addr.city,
            postal_code: addr.postal_code,
            insee_code: addr.insee_code ?? null,
            lat: addr.lat ?? null,
            lng: addr.lng ?? null,
          },
        },
      });
      const cov = res.data?.serviceEnDirectCheckAddress ?? null;
      setCoverage(cov);
      setCoverageChecked(true);
      // Switch the displayed carrier (name, deposit, services, …) to the
      // contractor that actually owns the closest depot. Apollo re-runs the
      // useQuery when this variable changes.
      if (cov?.contractor_id) {
        setSelectedContractorId(cov.contractor_id);
      }
    },
    [checkAddress],
  );

  if (paymentStatus === "success" && returnedReservationId) {
    return <PostPaymentRedirect reservationId={returnedReservationId} />;
  }

  if (loading) return <LoadingScreen />;

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">
          Erreur lors du chargement du service : {error.message}
        </Alert>
      </Container>
    );
  }
  if (!carrier) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="warning">
          Service non disponible. Vérifiez que{" "}
          <code>ServiceEnDirect.contractorCode</code> dans{" "}
          <code>config/app_local.php</code> correspond à un transporteur actif.
        </Alert>
      </Container>
    );
  }

  const handleConfirm = async () => {
    if (!canConfirm || !resolvedAddress || !selectedSlot) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const articlesPayload = selectedArticles.map((s) => ({
        article_id: s.service.id,
        quantity: s.quantity,
        customer_comment:
          s.comment && s.comment.trim() !== "" ? s.comment.trim() : null,
        customer_photos: (s.photos ?? []).map((p) => p.base64),
      }));
      const reserveRes = await reserve({
        variables: {
          input: {
            address: {
              address: resolvedAddress.address,
              city: resolvedAddress.city,
              postal_code: resolvedAddress.postal_code,
              lat: resolvedAddress.lat ?? null,
              lng: resolvedAddress.lng ?? null,
            },
            articles: articlesPayload,
            client,
            date: selectedSlot.date,
            halfday: selectedSlot.halfday,
          },
        },
      });
      const reservationId =
        reserveRes?.data?.serviceEnDirectReserve?.reservation_id;
      if (!reservationId) throw new Error("Reservation failed");

      if (carrier.paywall_enabled) {
        const baseUrl = `${window.location.origin}/service-en-direct`;
        const checkoutRes = await startCheckout({
          variables: {
            reservation_id: reservationId,
            success_url: `${baseUrl}?payment=success&rid=${reservationId}`,
            cancel_url: `${baseUrl}?payment=cancel&rid=${reservationId}`,
          },
        });
        const checkoutUrl =
          checkoutRes?.data?.serviceEnDirectStartCheckout?.checkout_url;
        if (checkoutUrl) window.location.replace(checkoutUrl);
      } else {
        const confirmRes = await confirmFree({
          variables: { reservation_id: reservationId },
        });
        const token = confirmRes?.data?.serviceEnDirectConfirmFree?.xct_token;
        if (token) {
          window.location.replace(
            `/reservations?xct=${encodeURIComponent(token)}`,
          );
        }
      }
    } catch (e: any) {
      setSubmitError(e?.message ?? "Une erreur est survenue");
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    {
      index: 1,
      label: "Adresse",
      sublabel: step1Done ? resolvedAddress?.formatted_address : undefined,
      state: (activeStep === 1
        ? "active"
        : step1Done
        ? "done"
        : "pending") as "active" | "done" | "pending",
    },
    {
      index: 2,
      label: `Articles${step2Done ? ` (${selectedArticles.length})` : ""}`,
      state: (activeStep === 2
        ? "active"
        : step2Done
        ? "done"
        : "pending") as "active" | "done" | "pending",
    },
    {
      index: 3,
      label: "Créneau",
      sublabel: step3Done
        ? `${selectedSlot?.date} — ${
            selectedSlot?.halfday === "am" ? "Matin" : "Après-midi"
          }`
        : undefined,
      state: (activeStep === 3
        ? "active"
        : step3Done
        ? "done"
        : "pending") as "active" | "done" | "pending",
    },
    {
      index: 4,
      label: "Coordonnées",
      state: (activeStep === 4
        ? "active"
        : "pending") as "active" | "done" | "pending",
    },
  ];

  const planningSlots = planningData?.serviceEnDirectPlanning?.slots ?? [];

  return (
    <PageContainer>
      <Container maxWidth="lg" disableGutters>
        <Box
          component="header"
          mb={3}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1.25, sm: 2 },
          }}
        >
          {carrier.logo_url && (
            <Box
              component="img"
              src={carrier.logo_url}
              alt={`Logo ${carrier.office_name ?? carrier.name}`}
              sx={{
                height: { xs: 44, sm: 64 },
                width: "auto",
                maxWidth: { xs: 120, sm: 200 },
                objectFit: "contain",
                flexShrink: 0,
                mr: { xs: 2, sm: 3 },
              }}
            />
          )}
          <PageTitle variant="h4" component="h1" sx={{ mb: 0 }}>
            Montage &amp; installation à domicile
          </PageTitle>
        </Box>

        <StepperHeader
          steps={steps}
          onStepClick={(i) => setActiveStep(i as 1 | 2 | 3 | 4)}
        />

        {activeStep === 1 && (
          <SectionCard>
            <SectionTitle component="h2">
              Votre adresse d&apos;intervention
            </SectionTitle>
            <SectionLead>
              Vos meubles sont arrivés ? Confiez le montage et l&apos;installation
              à nos techniciens. Saisissez votre adresse pour vérifier la
              disponibilité du service dans votre secteur.
            </SectionLead>
            <Stack spacing={2}>
              <AddressAutocomplete
                apiKey={carrier.google_maps_api_key}
                value={resolvedAddress}
                onChange={(addr) => {
                  setResolvedAddress(addr);
                  setCoverage(null);
                  setCoverageChecked(false);
                  if (addr) handleCheckAddress(addr);
                }}
              />
              {checkingAddress && (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  Vérification de la couverture…
                </Alert>
              )}
              {coverageChecked && coverage?.covered && (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  Bonne nouvelle ! Votre adresse est couverte par notre service.
                </Alert>
              )}
              {coverageChecked && !coverage?.covered && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  Votre adresse n&apos;est pas couverte par notre service pour
                  le moment.
                </Alert>
              )}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", sm: "flex-end" },
                }}
              >
                <PrimaryButton
                  size="large"
                  disabled={!step1Done}
                  onClick={() => setActiveStep(2)}
                >
                  Continuer
                </PrimaryButton>
              </Box>
            </Stack>
          </SectionCard>
        )}

        {activeStep === 2 && (
          <SectionCard>
            <ArticlesSection
              services={carrier.services}
              selected={selectedArticles}
              onChange={setSelectedArticles}
            />
            <Divider sx={{ my: 3 }} />
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column-reverse", sm: "row" },
                alignItems: "center",
                justifyContent: { xs: "center", sm: "space-between" },
                gap: 1.5,
              }}
            >
              <SecondaryButton onClick={() => setActiveStep(1)}>
                Retour
              </SecondaryButton>
              <PrimaryButton
                size="large"
                disabled={selectedArticles.length === 0}
                onClick={() => setActiveStep(3)}
              >
                Continuer
              </PrimaryButton>
            </Box>
          </SectionCard>
        )}

        {activeStep === 3 && (
          <>
            <SectionCard>
              <SectionTitle component="h2">
                Choisissez votre créneau d&apos;intervention
              </SectionTitle>
              <SectionLead>
                Sélectionnez le jour et le créneau horaire qui vous conviennent.
              </SectionLead>
              <PlanningCalendar
                slots={planningSlots}
                loading={planningLoading}
                selected={
                  selectedSlot
                    ? {
                        date: selectedSlot.date,
                        halfday: selectedSlot.halfday,
                      }
                    : null
                }
                onSelect={setSelectedSlot}
                contactEmail={carrier.office_email}
                contactPhone={carrier.office_phone}
              />
            </SectionCard>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column-reverse", sm: "row" },
                alignItems: "center",
                justifyContent: { xs: "center", sm: "space-between" },
                gap: 1.5,
                pb: { xs: 8, sm: 0 },
              }}
            >
              <SecondaryButton onClick={() => setActiveStep(2)}>
                Retour
              </SecondaryButton>
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <PrimaryButton
                  size="large"
                  disabled={!step3Done}
                  onClick={() => setActiveStep(4)}
                >
                  Continuer
                </PrimaryButton>
              </Box>
            </Box>

            {/* Sticky bottom CTA on phones */}
            <Box
              sx={{
                position: "fixed",
                left: 0,
                right: 0,
                bottom: 0,
                p: 1.25,
                bgcolor: "rgba(255,255,255,0.96)",
                borderTop: `1px solid ${TOKENS.GRAY_200}`,
                backdropFilter: "blur(8px)",
                display: { xs: "block", sm: "none" },
                zIndex: 10,
                boxShadow: "0 -4px 16px rgba(0,0,0,0.06)",
              }}
            >
              <PrimaryButton
                fullWidth
                size="large"
                disabled={!step3Done}
                onClick={() => setActiveStep(4)}
              >
                Continuer
              </PrimaryButton>
            </Box>
          </>
        )}

        {activeStep === 4 && (
          <>
            <SectionCard>
              <SectionTitle component="h2">Vos informations</SectionTitle>
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Prénom"
                    required
                    fullWidth
                    sx={inputSx}
                    value={client.firstName ?? ""}
                    onChange={(e) =>
                      setClient({ ...client, firstName: e.target.value })
                    }
                  />
                  <TextField
                    label="Nom"
                    required
                    fullWidth
                    sx={inputSx}
                    value={client.lastName ?? ""}
                    onChange={(e) =>
                      setClient({ ...client, lastName: e.target.value })
                    }
                  />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <PhoneField
                      value={client.phone ?? ""}
                      onChange={(e164) =>
                        setClient({ ...client, phone: e164 })
                      }
                      label="Téléphone *"
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TextField
                      label="Email"
                      required
                      type="email"
                      fullWidth
                      sx={inputSx}
                      value={client.email ?? ""}
                      onChange={(e) =>
                        setClient({ ...client, email: e.target.value })
                      }
                    />
                  </Box>
                </Stack>
                <TextField
                  label="Votre étage"
                  required
                  type="number"
                  sx={{
                    ...inputSx,
                    width: { xs: "100%", sm: 200 },
                  }}
                  value={client.floor ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setClient({
                      ...client,
                      floor: raw === "" ? undefined : Number(raw),
                    });
                  }}
                />
              </Stack>
            </SectionCard>

            <TotalsBar>
              {/* Itemized breakdown */}
              {selectedArticles.length > 0 && (
                <Box sx={{ position: "relative", mb: 1.5 }}>
                  {selectedArticles.map(({ service, quantity }, idx) => (
                    <Stack
                      key={service.id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="baseline"
                      sx={{
                        py: 0.75,
                        borderTop:
                          idx === 0
                            ? "none"
                            : `1px dashed ${TOKENS.GRAY_200}`,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="baseline"
                        sx={{ flex: 1, minWidth: 0 }}
                      >
                        <Typography
                          sx={{
                            color: TOKENS.GRAY_500,
                            fontWeight: 700,
                            fontSize: 12,
                            minWidth: 24,
                          }}
                        >
                          ×{quantity}
                        </Typography>
                        <Typography
                          sx={{
                            color: TOKENS.PRIMARY_BLUE,
                            fontWeight: 600,
                            fontSize: { xs: "0.85rem", sm: "0.9rem" },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {service.name}
                        </Typography>
                      </Stack>
                      <Typography
                        sx={{
                          color: TOKENS.GRAY_800,
                          fontWeight: 600,
                          fontSize: { xs: "0.85rem", sm: "0.9rem" },
                          ml: 1,
                        }}
                      >
                        {(service.price * quantity).toFixed(2)} €
                      </Typography>
                    </Stack>
                  ))}
                  <Divider sx={{ mt: 1 }} />
                </Box>
              )}

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: 12, sm: 14 },
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    color: TOKENS.GRAY_600,
                  }}
                >
                  {carrier.paywall_enabled && carrier.deposit_amount != null
                    ? "Sous-total prestations"
                    : "Total"}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "1.25rem", sm: "1.5rem" },
                    color: TOKENS.PRIMARY_BLUE,
                  }}
                >
                  {total.toFixed(2)} €
                </Typography>
              </Stack>
              {carrier.paywall_enabled &&
                carrier.deposit_amount != null && (
                  <>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mt: 1 }}
                    >
                      <Box sx={{ flex: 1, pr: 1.5 }}>
                        <Typography
                          sx={{
                            color: TOKENS.ACCENT_BLUE,
                            fontWeight: 600,
                          }}
                        >
                          Frais de déplacement
                        </Typography>
                        <Typography
                          sx={{
                            color: TOKENS.GRAY_600,
                            fontSize: "0.78rem",
                            lineHeight: 1.4,
                          }}
                        >
                          Facturés maintenant à la réservation, en plus du
                          montant des prestations.
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          color: TOKENS.ACCENT_BLUE,
                          fontWeight: 700,
                          fontSize: "1.1rem",
                        }}
                      >
                        {carrier.deposit_amount.toFixed(2)} €
                      </Typography>
                    </Stack>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: 12, sm: 14 },
                          letterSpacing: 0.5,
                          textTransform: "uppercase",
                          color: TOKENS.GRAY_600,
                        }}
                      >
                        Total
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: "1.25rem", sm: "1.5rem" },
                          color: TOKENS.PRIMARY_BLUE,
                        }}
                      >
                        {(total + carrier.deposit_amount).toFixed(2)} €
                      </Typography>
                    </Stack>
                    <Typography
                      sx={{
                        color: TOKENS.GRAY_600,
                        fontSize: "0.78rem",
                        lineHeight: 1.4,
                        mt: 1,
                      }}
                    >
                      Seuls les frais de déplacement (
                      {carrier.deposit_amount.toFixed(2)} €) sont prélevés
                      maintenant. Le montant des prestations (
                      {total.toFixed(2)} €) sera encaissé sur place le jour de
                      l&apos;intervention.
                    </Typography>
                  </>
                )}
            </TotalsBar>

            {submitError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {submitError}
              </Alert>
            )}

            {!canConfirm && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                {missingClientFields.length > 0
                  ? "Veuillez renseigner tous les champs obligatoires pour accéder au paiement."
                  : "Veuillez compléter les étapes précédentes pour accéder au paiement."}
              </Alert>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column-reverse", sm: "row" },
                alignItems: "center",
                justifyContent: { xs: "center", sm: "space-between" },
                gap: 1.5,
                pb: { xs: 8, sm: 0 },
              }}
            >
              <SecondaryButton onClick={() => setActiveStep(3)}>
                Retour
              </SecondaryButton>
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <PrimaryButton
                  size="large"
                  disabled={!canConfirm || submitting}
                  onClick={handleConfirm}
                >
                  {carrier.paywall_enabled
                    ? "Confirmer et payer les frais de déplacement"
                    : "Confirmer la réservation"}
                </PrimaryButton>
              </Box>
            </Box>

            {/* Sticky CTA on phones */}
            <Box
              sx={{
                position: "fixed",
                left: 0,
                right: 0,
                bottom: 0,
                p: 1.25,
                bgcolor: "rgba(255,255,255,0.96)",
                borderTop: `1px solid ${TOKENS.GRAY_200}`,
                backdropFilter: "blur(8px)",
                display: { xs: "block", sm: "none" },
                zIndex: 10,
                boxShadow: "0 -4px 16px rgba(0,0,0,0.06)",
              }}
            >
              <PrimaryButton
                fullWidth
                size="large"
                disabled={!canConfirm || submitting}
                onClick={handleConfirm}
              >
                {carrier.paywall_enabled
                  ? `Payer ${
                      carrier.deposit_amount?.toFixed(2) ?? ""
                    } € (frais de déplacement)`
                  : "Confirmer"}
              </PrimaryButton>
            </Box>
          </>
        )}

        <Box
          component="footer"
          sx={{
            mt: { xs: 4, sm: 6 },
            pt: 3,
            pb: { xs: 10, sm: 3 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            color: TOKENS.GRAY_600,
            textAlign: "center",
            borderTop: `1px solid ${TOKENS.GRAY_200}`,
          }}
        >
          {(carrier.footer_logo_url || carrier.logo_url) && (
            <Box
              component="img"
              src={carrier.footer_logo_url ?? carrier.logo_url ?? ""}
              alt={`Logo ${carrier.office_name ?? carrier.name}`}
              sx={{
                height: 64,
                width: "auto",
                maxWidth: 220,
                objectFit: "contain",
                opacity: 0.9,
              }}
            />
          )}
          <Typography sx={{ fontSize: "0.85rem", lineHeight: 1.5 }}>
            {[carrier.office_name, carrier.office_address]
              .filter(Boolean)
              .join(" — ")}
          </Typography>
          {carrier.office_phone && (
            <Typography
              component="a"
              href={`tel:${carrier.office_phone.replace(/\s+/g, "")}`}
              sx={{
                fontSize: "0.85rem",
                color: TOKENS.ACCENT_BLUE,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {carrier.office_phone}
            </Typography>
          )}
          {carrier.office_email && (
            <Typography
              component="a"
              href={`mailto:${carrier.office_email}`}
              sx={{
                fontSize: "0.85rem",
                color: TOKENS.ACCENT_BLUE,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {carrier.office_email}
            </Typography>
          )}
          {(() => {
            const links = LEGAL_DOCS.filter((d) =>
              legalHasContent(carrier[d.key]),
            );
            if (!links.length) return null;
            return (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1,
                  mt: 0.5,
                }}
              >
                {links.map((d, i) => (
                  <React.Fragment key={d.key}>
                    {i > 0 && (
                      <Typography
                        component="span"
                        sx={{ fontSize: "0.8rem", color: TOKENS.GRAY_400 }}
                      >
                        ·
                      </Typography>
                    )}
                    <Typography
                      component="button"
                      type="button"
                      onClick={() => setLegalDoc(d.key)}
                      sx={{
                        background: "none",
                        border: 0,
                        p: 0,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        fontSize: "0.8rem",
                        color: TOKENS.ACCENT_BLUE,
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {d.label}
                    </Typography>
                  </React.Fragment>
                ))}
              </Box>
            );
          })()}
          <Typography sx={{ fontSize: "0.7rem", color: TOKENS.GRAY_500, mt: 0.5 }}>
            Service propulsé par FluidIT
          </Typography>
        </Box>
      </Container>

      <LegalModal
        open={legalDoc !== null}
        title={LEGAL_DOCS.find((d) => d.key === legalDoc)?.title ?? ""}
        html={legalDoc ? carrier[legalDoc] : null}
        onClose={() => setLegalDoc(null)}
      />
      <CookieBanner
        onOpenPrivacy={
          legalHasContent(carrier.politique_confidentialite)
            ? () => setLegalDoc("politique_confidentialite")
            : undefined
        }
      />
    </PageContainer>
  );
};

const PostPaymentRedirect: React.FC<{
  reservationId: string;
}> = ({ reservationId }) => {
  const [finalize] = useMutation(SERVICE_EN_DIRECT_FINALIZE_PAYMENT);
  const [error, setError] = React.useState<string | null>(null);
  const attempts = React.useRef(0);

  React.useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const res = await finalize({
          variables: { reservation_id: reservationId },
        });
        const data = res?.data?.serviceEnDirectFinalizePayment;
        if (data?.paid && data.xct_token) {
          window.location.replace(
            `/reservations?xct=${encodeURIComponent(data.xct_token)}`,
          );
          return;
        }
      } catch (e: any) {
        if (attempts.current >= 6) {
          setError(e?.message ?? "Vérification du paiement impossible");
          return;
        }
      }
      // Try again every 2s, up to ~24s.
      if (attempts.current < 12) {
        timer = setTimeout(tick, 2000);
      } else {
        setError(
          "Le paiement met plus de temps que prévu à se confirmer. Vous recevrez un email dès qu'il sera validé.",
        );
      }
    };

    tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [reservationId, finalize]);

  return (
    <Container maxWidth="sm" sx={{ py: 8, textAlign: "center" }}>
      <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 700 }}>
        Paiement reçu
      </Typography>
      <Typography sx={{ mb: 3, color: "#4a5568" }}>
        Confirmation de votre réservation en cours…
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          color: "#3182ce",
        }}
      >
        <CircularProgress />
      </Box>
      {error && (
        <Alert severity="warning" sx={{ mt: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
    </Container>
  );
};

export default ServiceEnDirect;
